import { Mars, NonBinary, Venus } from "lucide-react";

import type { Company, Contact, ContactGender } from "../types";

export const defaultEmailJsonb = [{ email: null, type: null }];
export const defaultPhoneJsonb = [{ number: null, type: null }];

const cleanContactArrayFields = (data: Contact) => {
  const cleanedEmailJsonb =
    data.email_jsonb?.filter((e) => e.email != null) || [];
  const cleanedPhoneJsonb =
    data.phone_jsonb?.filter((p) => p.number != null) || [];
  return {
    ...data,
    phone_jsonb: cleanedPhoneJsonb.length > 0 ? cleanedPhoneJsonb : null,
    email_jsonb: cleanedEmailJsonb.length > 0 ? cleanedEmailJsonb : null,
  };
};

/**
 * Almikia Epic EC2a: force-nulls every dependent field whose contact type is not selected
 * (mirror of the stale cleanup in `enforce_contacts_ec2a_guard`): colega fields without the
 * 'colega' type, the supplier category without 'supplier', the guarantor link without
 * 'guarantor', the project when the business unit is not 'bpo'. Fields absent from `data` are
 * left absent (a partial update stays partial). Returns a new object.
 */
export const cleanupContactDependentFields = <T extends object>(data: T): T => {
  const next = { ...data } as Record<string, unknown>;
  const types = Array.isArray(next.contact_types) ? next.contact_types : [];
  const nullIfPresent = (...fields: string[]) => {
    for (const field of fields) {
      if (field in next) next[field] = null;
    }
  };
  if (!types.includes("colega")) {
    nullIfPresent(
      "colega_kind",
      "colega_affiliation",
      "colega_agreement_signed",
    );
  }
  if (!types.includes("supplier")) nullIfPresent("supplier_category_id");
  if (!types.includes("guarantor")) nullIfPresent("guarantor_of_contact_id");
  if (next.business_unit !== "bpo") nullIfPresent("business_project_id");
  return next as T;
};

const LEGACY_CONTACT_FIELDS = [
  "status",
  "linkedin_url",
  "lead_source_name",
  "supplier_category_name",
  "business_project_name",
] as const;

/**
 * Almikia Epic EC2a: drops the legacy `status` and `linkedin_url` (kept in the database, no
 * longer written) and the summary-only joined names from a payload. Returns a new object.
 */
export const stripLegacyContactFields = <T extends object>(
  data: T,
): Omit<T, (typeof LEGACY_CONTACT_FIELDS)[number]> =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([key]) => !(LEGACY_CONTACT_FIELDS as readonly string[]).includes(key),
    ),
  ) as Omit<T, (typeof LEGACY_CONTACT_FIELDS)[number]>;

const cleanupContactPayload = (data: Contact) =>
  stripLegacyContactFields(
    cleanupContactDependentFields(cleanContactArrayFields(data)),
  );

export const cleanupContactForCreate = (data: Contact) => {
  return cleanupContactPayload({
    ...data,
    first_seen: new Date().toISOString(),
    last_seen: new Date().toISOString(),
    tags: data.tags ?? [],
  });
};

export const cleanupContactForEdit = cleanupContactPayload;

/** Full display name: Nombre(s) + Apellido paterno + Apellido materno, tolerant of nulls. */
export const contactFullName = (
  contact:
    | Pick<Contact, "first_name" | "last_name" | "second_last_name">
    | null
    | undefined,
): string =>
  [contact?.first_name, contact?.last_name, contact?.second_last_name]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0)
    .join(" ");

/** Surname particles that stop an apellido materno from being split off (mirror of SQL). */
const SURNAME_PARTICLES = new Set([
  "de",
  "del",
  "la",
  "las",
  "los",
  "y",
  "san",
  "santa",
  "van",
  "von",
  "da",
  "di",
  "mac",
  "mc",
]);

export type SplitSurnames = {
  paterno: string | null;
  materno: string | null;
  needsReview: boolean;
};

/**
 * TypeScript mirror of `private.split_surnames`: the apellido materno is split off ONLY when the
 * surname is exactly two plain tokens (none a particle). Every other surname stays whole in
 * `paterno`, `materno` is null and `needsReview` is true; an empty surname is flagged too.
 */
export const splitSurnames = (
  lastName: string | null | undefined,
): SplitSurnames => {
  const surname = (lastName ?? "").replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, "");
  if (surname.length === 0) {
    return { paterno: null, materno: null, needsReview: true };
  }
  const tokens = surname.split(/[ \t\r\n]+/);
  const isPlainPair =
    tokens.length === 2 &&
    !tokens.some((token) => SURNAME_PARTICLES.has(token.toLowerCase()));
  return isPlainPair
    ? { paterno: tokens[0], materno: tokens[1], needsReview: false }
    : { paterno: surname, materno: null, needsReview: true };
};

/** Spanish messages of the mandatory-field rules; identical to the DB guard's exceptions. */
export const CONTACT_MANDATORY_MESSAGES = {
  contact_types: "Elige al menos un tipo de contacto",
  first_name: "El nombre es obligatorio",
  last_name: "El apellido paterno es obligatorio",
  second_last_name: "El apellido materno es obligatorio",
  lead_source_id: "Elige el origen del contacto",
  business_unit: "Elige la unidad de negocio",
  business_project_id: "Elige el proyecto de la unidad BPO",
  referred_by_contact_id: "Elige el contacto que refirio",
  origin_colega_contact_id: "Elige al colega de origen",
  guarantor_of_contact_id: "Elige al inquilino al que avala el aval",
  colega_kind: "Elige si el colega es independiente o inmobiliaria",
  company_id: "Elige la empresa de la inmobiliaria",
  colega_agreement_signed: "Indica si el colega firmo el convenio",
  supplier_category_id: "Elige la categoria del proveedor",
} as const;

export type ContactMandatoryField = keyof typeof CONTACT_MANDATORY_MESSAGES;
export type ContactMandatoryErrors = Partial<
  Record<ContactMandatoryField, string>
>;

const isBlank = (value: string | null | undefined): boolean =>
  (value ?? "").trim().length === 0;

/**
 * Validates the mandatory fields of a contact, mirroring `enforce_contacts_ec2a_guard` (a full
 * check, as on insert). Returns the Spanish message per failing field; an empty object means
 * valid. `originLinkKind` is the `link_kind` of the chosen origin (`lead_sources`), which decides
 * whether the referring contact or the origin colega is required.
 */
export const validateContactMandatory = (
  values: Partial<Contact>,
  options: { originLinkKind?: "contact" | "colega" | null } = {},
): ContactMandatoryErrors => {
  const errors: ContactMandatoryErrors = {};
  const fail = (field: ContactMandatoryField) => {
    errors[field] = CONTACT_MANDATORY_MESSAGES[field];
  };
  const types = values.contact_types ?? [];

  if (types.length === 0) fail("contact_types");
  if (isBlank(values.first_name)) fail("first_name");
  if (isBlank(values.last_name)) fail("last_name");
  if (isBlank(values.second_last_name)) fail("second_last_name");
  if (values.lead_source_id == null) fail("lead_source_id");
  if (values.business_unit == null) fail("business_unit");
  if (values.business_unit === "bpo" && values.business_project_id == null) {
    fail("business_project_id");
  }
  if (
    values.lead_source_id != null &&
    options.originLinkKind === "contact" &&
    values.referred_by_contact_id == null
  ) {
    fail("referred_by_contact_id");
  }
  if (
    values.lead_source_id != null &&
    options.originLinkKind === "colega" &&
    values.origin_colega_contact_id == null
  ) {
    fail("origin_colega_contact_id");
  }
  if (types.includes("guarantor") && values.guarantor_of_contact_id == null) {
    fail("guarantor_of_contact_id");
  }
  if (types.includes("colega")) {
    if (values.colega_kind == null) fail("colega_kind");
    if (values.colega_kind === "inmobiliaria" && values.company_id == null) {
      fail("company_id");
    }
    if (values.colega_agreement_signed == null) fail("colega_agreement_signed");
  }
  if (types.includes("supplier") && values.supplier_category_id == null) {
    fail("supplier_category_id");
  }
  return errors;
};

type TranslateFn = (key: string, options?: { [key: string]: any }) => string;

export const contactGenderDefaultLabels: Record<string, string> = {
  male: "He/Him",
  female: "She/Her",
  nonbinary: "They/Them",
};

const personalInfoTypeMap: Record<string, string> = {
  Work: "work",
  Home: "home",
  Other: "other",
};

export const contactGender: ContactGender[] = [
  {
    value: "male",
    label: "resources.contacts.inputs.genders.male",
    icon: Mars,
  },
  {
    value: "female",
    label: "resources.contacts.inputs.genders.female",
    icon: Venus,
  },
  {
    value: "nonbinary",
    label: "resources.contacts.inputs.genders.nonbinary",
    icon: NonBinary,
  },
];

export const translateContactGenderLabel = (
  gender: { value: string; label: string },
  translate: TranslateFn,
) =>
  translate(gender.label, {
    _: contactGenderDefaultLabels[gender.value] ?? gender.label,
  });

export const translatePersonalInfoTypeLabel = (
  type: string,
  translate: TranslateFn,
) =>
  translate(
    `resources.contacts.inputs.personal_info_types.${personalInfoTypeMap[type] ?? type.toLowerCase()}`,
    {
      _: type,
    },
  );

/**
 * Folds a long line according to vCard specification (max 75 chars per line)
 * Continuation lines start with a space
 */
function foldLine(line: string): string {
  const maxLength = 75;
  if (line.length <= maxLength) return line;

  const result: string[] = [];
  let currentLine = line.substring(0, maxLength);
  let remaining = line.substring(maxLength);

  result.push(currentLine);

  while (remaining.length > 0) {
    // Continuation lines start with a space and can have 74 more chars
    const chunkSize = maxLength - 1;
    currentLine = " " + remaining.substring(0, chunkSize);
    remaining = remaining.substring(chunkSize);
    result.push(currentLine);
  }

  return result.join("\r\n");
}

/**
 * Converts a contact and their company to vCard 3.0 format
 */
export function exportToVCard(
  contact: Contact,
  company?: Company,
  photoData?: { base64: string; mimeType: string },
): string {
  const lines: string[] = [];

  // vCard header
  lines.push("BEGIN:VCARD");
  lines.push("VERSION:3.0");

  // Name (N: Family Name;Given Name;Additional Names;Honorific Prefixes;Honorific Suffixes)
  // (the family name carries both surnames: Apellido paterno and Apellido materno)
  const familyName = [contact.last_name, contact.second_last_name]
    .filter((part) => part && part.trim().length > 0)
    .join(" ");
  lines.push(`N:${familyName};${contact.first_name};;;`);

  // Formatted name
  lines.push(`FN:${contactFullName(contact)}`);

  // Title/Job position
  if (contact.title) {
    lines.push(`TITLE:${contact.title}`);
  }

  // Organization
  if (company?.name) {
    lines.push(`ORG:${company.name}`);
  }

  // Emails
  if (contact.email_jsonb && contact.email_jsonb.length > 0) {
    contact.email_jsonb.forEach((emailObj) => {
      const type = emailObj.type.toUpperCase();
      lines.push(`EMAIL;TYPE=${type}:${emailObj.email}`);
    });
  }

  // Phone numbers
  if (contact.phone_jsonb && contact.phone_jsonb.length > 0) {
    contact.phone_jsonb.forEach((phoneObj) => {
      const type = phoneObj.type.toUpperCase();
      lines.push(`TEL;TYPE=${type}:${phoneObj.number}`);
    });
  }

  // Background/Note
  if (contact.background) {
    // Escape newlines and special characters in notes
    const escapedNote = contact.background
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");
    lines.push(`NOTE:${escapedNote}`);
  }

  // Photo/Avatar - vCard 3.0 format with base64 encoding
  if (photoData) {
    // Extract image type from MIME type (e.g., "image/png" -> "PNG")
    const imageType = photoData.mimeType.split("/")[1]?.toUpperCase() || "PNG";

    // vCard 3.0 format: PHOTO;ENCODING=b;TYPE=PNG:
    const photoLine = `PHOTO;ENCODING=b;TYPE=${imageType}:${photoData.base64}`;
    lines.push(foldLine(photoLine));
  }

  // vCard footer
  lines.push("END:VCARD");

  return lines.join("\r\n");
}
